import React from 'react';
import {
  ApiKeyUsageDetail,
  ModelUsageDetail,
} from '../../../utils/openai-api';
import { formatNumber, formatCurrency } from '../utils/formatters';
import { getModelUsageDisplay } from '../utils/modelUsageDisplay';

// --- Split by source sub-components ---

const ModelUsageTable: React.FC<{ modelUsage: Record<string, ModelUsageDetail>; label?: string }> = ({ modelUsage, label }) => {
  const entries = Object.values(modelUsage);
  if (entries.length === 0) return null;
  return (
    <div>
      {label && (
        <h5 className="text-xs font-semibold text-indigo-300 mb-1">{label}</h5>
      )}
      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Model</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Context Tokens</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Generated Tokens</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cached Tokens</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cost ($)</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Requests / Searches</th>
              <th className="px-2 sm:px-3 py-1 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Used</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {entries.map((modelData, modelIdx) => {
              const display = getModelUsageDisplay(modelData);
              return (
              <tr key={modelData.model} className={modelIdx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                <td className="px-2 sm:px-3 py-1 text-xs font-medium text-gray-900 dark:text-white">
                  {modelData.model}
                  {display.description && <span className="block text-[11px] font-normal text-slate-500 dark:text-slate-400">{display.description}</span>}
                </td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">{display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(modelData.contextTokens)}</td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">{display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(modelData.generatedTokens)}</td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">{display.isSearchCharge || display.isSearchActivity ? '—' : formatNumber(modelData.cachedTokens)}</td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">
                  {display.isSearchActivity ? '別行に計上' : formatCurrency(modelData.cost)}
                  {display.isSearchCharge && <span className="block text-[11px]">配賦額</span>}
                </td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">
                  {display.quantity === null ? '不明' : formatNumber(display.quantity)}
                  {display.unit && <span className="ml-1 text-[11px]">{display.unit}</span>}
                </td>
                <td className="px-2 sm:px-3 py-1 text-xs text-gray-500 dark:text-gray-400">{modelData.lastUsed ?? '—'}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {entries.some(model => getModelUsageDisplay(model).isSearchCharge) && (
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">キー別の検索料金は配賦した推定額です。検索回数はキー単位では取得していません。</p>
      )}
    </div>
  );
};

export const ModelUsageContent: React.FC<{ apiKey: ApiKeyUsageDetail; isSplit: boolean }> = ({ apiKey, isSplit }) => {
  if (!isSplit || !apiKey.mergedSources || apiKey.mergedSources.length <= 1) {
    if (Object.keys(apiKey.modelUsage).length === 0) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No model usage data available for this API key.
        </p>
      );
    }
    return <ModelUsageTable modelUsage={apiKey.modelUsage} />;
  }

  // Split view: group mergedSources by workspace
  const sourcesByWorkspace = new Map<string, ApiKeyUsageDetail[]>();
  for (const src of apiKey.mergedSources) {
    const wsName = src.projectName || 'Unknown';
    const list = sourcesByWorkspace.get(wsName) || [];
    list.push(src);
    sourcesByWorkspace.set(wsName, list);
  }

  const sections: { label: string; modelUsage: Record<string, ModelUsageDetail> }[] = [];
  for (const [wsName, sources] of sourcesByWorkspace) {
    const merged: Record<string, ModelUsageDetail> = {};
    for (const src of sources) {
      for (const [model, detail] of Object.entries(src.modelUsage)) {
        if (merged[model]) {
          merged[model] = {
            ...merged[model],
            contextTokens: merged[model].contextTokens + detail.contextTokens,
            generatedTokens: merged[model].generatedTokens + detail.generatedTokens,
            cachedTokens: merged[model].cachedTokens + detail.cachedTokens,
            cost: merged[model].cost + detail.cost,
            numberOfRequests: merged[model].numberOfRequests + detail.numberOfRequests,
            lastUsed: detail.lastUsed && (!merged[model].lastUsed || detail.lastUsed > merged[model].lastUsed)
              ? detail.lastUsed : merged[model].lastUsed,
          };
        } else {
          merged[model] = { ...detail };
        }
      }
    }
    sections.push({ label: wsName, modelUsage: merged });
  }

  if (sections.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No model usage data available for this API key.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {sections.map(({ label, modelUsage }) => (
        <ModelUsageTable key={label} modelUsage={modelUsage} label={label} />
      ))}
    </div>
  );
};
